/**
 * WanLocalInstallSection
 *
 * Static install cards for the four Wan local components:
 *   comfyui_gguf, wan21_1_3b, wan22_5b, wan22_14b
 *
 * When install_blocked_by_vram (Phase 3B backend flag) is true, or when the
 * user's detected VRAM is below the required minimum, the Install button is
 * rendered disabled with a clear "Requires X GB VRAM (you have Y GB)" message.
 * No yellow warning banner. No "close apps" hint. Direct gating only.
 *
 * Phase 3B: wire `installBlocked`/`requiredVram`/`detectedVram` to the
 * availability endpoint response. Until then, they are derived from
 * useSystemGpuInfo() and the hardcoded per-variant VRAM requirements.
 */

import React from 'react';
import { Download, CheckCircle } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useSystemGpuInfo } from '../../hooks/useSystemGpuInfo';
import { useComponentStatus } from '../../hooks/useComponentStatus';
import { WAN_VARIANT_SPECS } from '../../constants/wanLocal';

/** VRAM requirement per component. comfyui_gguf has no VRAM requirement. */
const COMPONENT_MIN_VRAM: Record<string, number> = {
  comfyui_gguf: 0,
  wan21_1_3b: WAN_VARIANT_SPECS.wan21_1_3b.minVramGb,
  wan22_5b: WAN_VARIANT_SPECS.wan22_5b.minVramGb,
  wan22_14b: WAN_VARIANT_SPECS.wan22_14b.minVramGb,
};

interface WanInstallCardProps {
  componentId: string;
  title: string;
  description: string;
  tier: string;
  size: string;
  isPremium?: boolean;
  detectedVramGb: number | null;
}

const WanInstallCard: React.FC<WanInstallCardProps> = ({
  componentId,
  title,
  description,
  tier,
  size,
  isPremium = false,
  detectedVramGb,
}) => {
  const { t } = useTranslation();
  const status = useComponentStatus(componentId);
  const requiredVram = COMPONENT_MIN_VRAM[componentId] ?? 0;
  const detected = detectedVramGb ?? 0;

  // VRAM gate: block install when detected < required.
  // comfyui_gguf (requiredVram === 0) is never blocked.
  const installBlockedByVram = requiredVram > 0 && detected < requiredVram && detected > 0;

  const isInstalled = status?.is_installed === true;
  const isRunning = status?.is_running === true;

  return (
    <div
      className={[
        'rounded-xl border p-4 space-y-3 transition-colors',
        installBlockedByVram
          ? 'border-white/10 bg-black/20 opacity-70'
          : 'border-white/10 bg-black/30',
      ].join(' ')}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white/90">{title}</span>
            {isPremium && (
              <span className="text-[10px] text-purple-300/80 font-medium">
                {t('componentSetup.wan22_14b.tier', {
                  defaultValue: 'Premium tier — RTX 4090 24 GB+',
                })}
              </span>
            )}
            {isInstalled && (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium">
                <CheckCircle className="w-3 h-3" />
                {t('componentSetup.status.installed', { defaultValue: 'Installed' })}
                {isRunning && (
                  <span className="text-emerald-300/70">
                    {' '}
                    &bull; {t('componentSetup.status.running', { defaultValue: 'Running' })}
                  </span>
                )}
              </span>
            )}
          </div>
          <p className="text-xs text-white/50 mt-0.5">{description}</p>
        </div>
        <span className="flex-shrink-0 text-[10px] text-white/40 font-medium">{tier}</span>
      </div>

      {/* Size */}
      <p className="text-[10px] text-white/40">{size}</p>

      {/* VRAM block message or Install button */}
      {installBlockedByVram ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-red-400/80">
            {t('componentSetup.wan.vramBlocked', {
              defaultValue: 'Requires {{required}} GB VRAM (you have {{detected}} GB)',
              required: requiredVram,
              detected: Math.round(detected),
            })}
          </span>
          <button
            type="button"
            disabled
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white/5 text-white/30 border border-white/10 cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" />
            {t('componentSetup.options.install.button', { defaultValue: 'Install' })}
          </button>
        </div>
      ) : isInstalled ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-emerald-400/70">
            {t('componentSetup.status.installed', { defaultValue: 'Installed' })}
          </span>
        </div>
      ) : (
        <button
          type="button"
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 border border-purple-500/30 transition-colors"
          onClick={() => {
            // Phase 3B: wire to installComponent(componentId) from useComponents hook
            // For now, navigate to Settings > Installations via event.
            window.dispatchEvent(new CustomEvent('install-component', { detail: { componentId } }));
          }}
        >
          <Download className="w-3.5 h-3.5" />
          {t('componentSetup.options.install.button', { defaultValue: 'Install' })}
        </button>
      )}
    </div>
  );
};

/**
 * Section rendered in AdditionalInstallationsPage after existing components.
 * Shows 4 Wan install cards with VRAM gating.
 */
export const WanLocalInstallSection: React.FC = () => {
  const { t } = useTranslation();
  const { gpuInfo } = useSystemGpuInfo();
  const detectedVram = gpuInfo?.vram_gb ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-white/90">
          {t('componentSetup.wanSection.title', { defaultValue: 'Local Wan Video Generation' })}
        </h2>
        <p className="text-xs text-white/50 mt-0.5">
          {t('componentSetup.wanSection.description', {
            defaultValue:
              'Run Wan AI video models locally — completely free, no cloud costs. Requires an NVIDIA GPU.',
          })}
        </p>
      </div>

      <WanInstallCard
        componentId="comfyui_gguf"
        title={t('componentSetup.comfyuiGguf.title', { defaultValue: 'ComfyUI GGUF' })}
        description={t('componentSetup.comfyuiGguf.description', {
          defaultValue:
            'ComfyUI custom node for GGUF-quantized models. Required by all local Wan providers.',
        })}
        tier={t('componentSetup.comfyuiGguf.tier', { defaultValue: 'Prerequisite' })}
        size="~50 MB"
        detectedVramGb={detectedVram}
      />

      <WanInstallCard
        componentId="wan21_1_3b"
        title={t('componentSetup.wan21_1_3b.title', { defaultValue: 'Wan 2.1 1.3B' })}
        description={t('componentSetup.wan21_1_3b.description', {
          defaultValue: 'Lightweight local T2V — 480p output, fast generation on mid-range GPUs.',
        })}
        tier={t('componentSetup.wan21_1_3b.tier', { defaultValue: '8 GB VRAM+' })}
        size="~1.1 GB"
        detectedVramGb={detectedVram}
      />

      <WanInstallCard
        componentId="wan22_5b"
        title={t('componentSetup.wan22_5b.title', { defaultValue: 'Wan 2.2 5B (TI2V)' })}
        description={t('componentSetup.wan22_5b.description', {
          defaultValue:
            'Versatile local T2V and I2V — native 720p output. A single model for both modes.',
        })}
        tier={t('componentSetup.wan22_5b.tier', { defaultValue: '12 GB VRAM+' })}
        size="~3 GB"
        detectedVramGb={detectedVram}
      />

      <WanInstallCard
        componentId="wan22_14b"
        title={t('componentSetup.wan22_14b.title', { defaultValue: 'Wan 2.2 14B (SOTA)' })}
        description={t('componentSetup.wan22_14b.description', {
          defaultValue:
            'Premium state-of-the-art local T2V with LightX2V Lightning LoRA — 4-step generation.',
        })}
        tier={t('componentSetup.wan22_14b.tier', { defaultValue: '20 GB VRAM+' })}
        size="~20 GB"
        isPremium
        detectedVramGb={detectedVram}
      />
    </div>
  );
};

export default WanLocalInstallSection;
